import React, { useEffect, useState } from 'react';
import { ShieldCheck, Target, Crosshair, Gauge, MessageSquareText, FileCheck2, TrendingUp, Layers } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { KpiTile } from '@/components/common/KpiTile';
import { Loader, EmptyState } from '@/components/common/Feedback';
import api from '@/lib/api';
import { fmtMoney, fmtPct, SOURCE_LABEL, VERDICT_LABEL } from '@/lib/format';
import { REASON_OPTIONS } from '@/lib/format';

const reasonLabel = (v) => (REASON_OPTIONS.find((r) => r.value === v)?.label) || v;

export default function Validation() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get('/validation/company').then((r) => setD(r.data)).finally(() => setLoading(false)); }, []);

  if (loading) return <Loader label="Loading validation metrics..." />;
  if (!d) return null;
  const opp = d.estimated_opportunity || {};

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">Validation</h2>
        <p className="text-sm text-slate-500">How well the AI performs on your projects, based on your feedback. {d.total_judged} judged.</p>
      </div>

      {d.total_judged === 0 ? (
        <EmptyState icon={ShieldCheck} title="No feedback yet" description="Review analyzed events and mark them Correct / Incorrect to build your validation evidence." />
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <KpiTile testid="val-agreement" label="Agreement rate" value={fmtPct(d.agreement_rate)} isMoney={false} variant="recovered" icon={ShieldCheck} meta={`${d.correct} of ${d.total_judged} accepted as-is`} />
            <KpiTile testid="val-precision" label="Precision" value={fmtPct(d.precision)} isMoney={false} variant="info" icon={Target} meta={`${d.tp} TP / ${d.fp} FP`} />
            <KpiTile testid="val-recall" label="Recall" value={fmtPct(d.recall)} isMoney={false} variant="info" icon={Crosshair} meta={`${d.fn} false negatives`} />
            <KpiTile testid="val-confidence" label="Avg AI confidence" value={fmtPct(d.avg_confidence)} isMoney={false} variant="neutral" icon={Gauge} meta={`Would-submit ${fmtPct(d.would_submit_rate)}`} />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[['Events', d.events], ['Detections', d.detections], ['Change orders', d.change_orders], ['False positives', d.false_positives]].map(([l, v]) => (
              <Card key={l} className="p-4"><div className="text-[11px] uppercase tracking-wide text-slate-400">{l}</div><div className="text-2xl font-semibold font-mono text-slate-800 mt-1">{v}</div></Card>
            ))}
          </div>

          <Card className="p-5">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-800"><TrendingUp className="h-4 w-4" />Estimated commercial opportunity</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-3">
              <div><div className="text-[11px] uppercase tracking-wide text-slate-400">AI-detected potential</div><div className="text-2xl font-semibold font-mono text-warn-700">{fmtMoney(opp.ai_detected_value)}</div></div>
              <div><div className="text-[11px] uppercase tracking-wide text-slate-400">Contractor-confirmed</div><div className="text-2xl font-semibold font-mono text-money-700">{fmtMoney(opp.confirmed_value)}</div></div>
              <div><div className="text-[11px] uppercase tracking-wide text-slate-400">Confirmed opportunities</div><div className="text-2xl font-semibold font-mono text-slate-800">{opp.confirmed_count || 0}</div></div>
            </div>
            <p className="text-[11px] text-slate-400 mt-2">Estimates of potential entitlement identified &mdash; NOT recovered revenue.</p>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Card className="p-5">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-800 mb-3"><Layers className="h-4 w-4" />Performance by document type</div>
              {d.by_source_type?.length ? d.by_source_type.map((s) => (
                <div key={s.source_type} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0 text-sm">
                  <span className="text-slate-700">{SOURCE_LABEL[s.source_type] || s.source_type} <span className="text-slate-400">({s.judged})</span></span>
                  <span className="font-mono font-semibold text-slate-800">{fmtPct(s.agreement_rate)}</span>
                </div>
              )) : <div className="text-xs text-slate-400">No data yet.</div>}
            </Card>
            <Card className="p-5">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-800 mb-3"><FileCheck2 className="h-4 w-4" />Most common failure reasons</div>
              {d.reason_breakdown?.length ? d.reason_breakdown.map((r) => (
                <div key={r.reason} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0 text-sm">
                  <span className="text-slate-700">{reasonLabel(r.reason)}</span><span className="font-mono font-semibold text-danger-700">{r.count}</span>
                </div>
              )) : <div className="text-xs text-slate-400">No disagreements recorded — great sign.</div>}
            </Card>
          </div>

          <Card className="p-5">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-800 mb-3"><MessageSquareText className="h-4 w-4" />Recent feedback comments</div>
            {d.recent_comments?.length ? (
              <div className="space-y-2">
                {d.recent_comments.map((c, i) => (
                  <div key={i} className="text-sm bg-slate-50 rounded-lg px-3 py-2">
                    <span className="font-semibold text-slate-700">{VERDICT_LABEL[c.verdict]}:</span> <span className="text-slate-600">“{c.comment}”</span>
                  </div>
                ))}
              </div>
            ) : <div className="text-xs text-slate-400">No comments yet.</div>}
          </Card>
        </>
      )}
    </div>
  );
}
